import {
  WeightSet,
  CardioSet,
  RepsSet,
  DurationSet,
  WeightedDurationSet,
} from "./sets.interface";
import { BodyPart } from "./bodypart.interface";
import { calculateOneRepMax } from "./workout.interface";
import { ExerciseTemplate } from "./workoutTemplate.interface";

export type Exercise = Weights;
// Todo: add these back later
// Cardio | Reps | Duration | WeightedDuration;

export interface ExerciseBase {
  exerciseName: string;
  bodyParts: BodyPart[];
  exerciseNote?: string;
  // Helpers when displaying data
  showExercise?: boolean;
  wantExerciseNote?: boolean;
}

// Types Of Exercises
export interface Weights extends ExerciseBase {
  type:
    | "Barbell"
    | "Dumbbell"
    | "Machine"
    | "Weighted Bodyweight"
    | "Assisted Bodyweight";
  sets: WeightSet[];
}

export interface Cardio extends ExerciseBase {
  type: "Cardio";
  sets: CardioSet[];
}

export interface Reps extends ExerciseBase {
  type: "Reps";
  sets: RepsSet[];
}

export interface Duration extends ExerciseBase {
  type: "Duration";
  sets: DurationSet[];
}

export interface WeightedDuration extends ExerciseBase {
  type: "Weighted Duration";
  sets: WeightedDurationSet[];
}

export interface AddNewExercise {
  exerciseName: string;
  bodyParts: BodyPart[];
  type: AllTypesOfExercises;
  wantToAdd: boolean;
  text?: string; // used for v-autocomplete
}

export type AllTypesOfExercises =
  | "Barbell"
  | "Dumbbell"
  | "Machine"
  | "Weighted Bodyweight"
  | "Assisted Bodyweight";
// | "Cardio"
// | "Reps"
// | "Duration"
// | "Weighted Duration";

export interface AddNewExercise {
  exerciseName: string;
  bodyParts: BodyPart[];
  type: AllTypesOfExercises;
  wantToAdd: boolean;
}

export interface NewExericse {
  exerciseName: string;
  type: AllTypesOfExercises;
  bodyParts: BodyPart[];
  isCustomExercise: boolean;
}

export interface SetEmitData {
  exerciseIdx: number;
  setIdx: number;
  setChangeType: SetChangeType;
  addNote?: boolean;
  newValues?: WeightSet;
}

export type SetChangeType =
  | "Add Set"
  | "Remove Set"
  | "Replace Set Values"
  | "Set Completion"
  | "Set Video"
  | "Set Note";

export interface ExerciseEmitData {
  setIdx: number;
  exerciseIdx: number;
  wantVideo?: boolean;
  addNote?: boolean;
  video?: Blob | undefined;
}

export interface ExerciseFirestore extends NewExericse {
  datesDone: string[];
}

export type ExerciseTags = ["Push", "Pull", "Legs"];

export function removeExerciseClientHelpers(
  exerciseWithClientHelpers: Exercise[]
): Exercise[] {
  let formatedExercies: Exercise[] = [];

  // Removes sets that were not done,
  // and exercises that did not have a single done set
  exerciseWithClientHelpers.forEach((exercise) => {
    // Deletes sets.done property
    const formattedSets: any[] = []; // todo: make this of type exercise sets

    for (let i = 0; i < exercise.sets.length; i++) {
      if (exercise.sets[i].done) {
        delete exercise.sets[i].done;
        formattedSets.push(exercise.sets[i]);
      }
    }

    const formatedExercise: Exercise = {
      bodyParts: exercise.bodyParts,
      exerciseName: exercise.exerciseName,
      type: exercise.type,
      sets: formattedSets,
    };

    formatedExercies.push(formatedExercise);
  });

  // Removes exercises that have 0 sets done
  formatedExercies = formatedExercies.filter((exercise) => {
    return exercise.sets.length > 0;
  });

  return formatedExercies;
}

export function getExerciseType(
  exercise: Exercise
): "Cardio" | "Reps" | "Duration" | "Weights" | undefined {
  const exerciseType = exercise.type;
  // if (exerciseType === "Cardio") return "Cardio";
  // if (exerciseType === "Reps") return "Reps";
  // if (exerciseType === "Duration") return "Duration";
  if (
    exerciseType === "Barbell" ||
    exerciseType === "Dumbbell" ||
    exerciseType === "Machine" ||
    exerciseType === "Weighted Bodyweight" ||
    exerciseType === "Assisted Bodyweight"
  )
    return "Weights";

  return undefined;
}

export function formatExerciseId(exerciseName: string, type: string): string {
  return `${exerciseName} | ${type}`;
}

export function formatExerciseVideoId(
  startTime: number,
  setIdx: number | string
): string {
  return `${startTime} | Set:${setIdx}`;
}

export function getBestWeightedExerciseSet(exerciseSets: WeightSet[]): string {
  const bestCalculatedWeightSet = {
    oneRepMax: 0,
    output: "",
  };

  exerciseSets.forEach((set) => {
    if (Number(set.weight) === 0 && Number(set.reps) > 0) {
      if (Number(set.reps) > bestCalculatedWeightSet.oneRepMax) {
        bestCalculatedWeightSet.oneRepMax = Number(set.reps);
        bestCalculatedWeightSet.output = `${set.reps} Reps`;
      }
    } else {
      const setOneRepMax: number = calculateOneRepMax(set.weight, set.reps);
      if (setOneRepMax > bestCalculatedWeightSet.oneRepMax) {
        bestCalculatedWeightSet.oneRepMax = setOneRepMax;
        bestCalculatedWeightSet.output = `${set.weight} x ${set.reps}`;
      }
    }
  });

  console.log(bestCalculatedWeightSet);

  return bestCalculatedWeightSet.output;
}

export function getBestSetsFromExerciseTemplates(
  exercises?: ExerciseTemplate[]
): string[] {
  const bestSetList: string[] = [];

  if (exercises) {
    exercises.forEach((exercise) => {
      const bestSet = {
        output: "",
        calculated: 0,
      };

      const weightedSets = [
        "Barbell",
        "Dumbbell",
        "Machine",
        "Weighted Bodyweight",
        "Assisted Bodyweight",
      ];

      if (weightedSets.includes(exercise.type)) {
        if (exercise.sets[0].weight && exercise.sets[0].reps) {
          bestSet.output = `${exercise.sets[0].weight} x ${exercise.sets[0].reps}`;
          bestSet.calculated = exercise.sets[0].weight * exercise.sets[0].reps;
        }
      }

      if (exercise.sets.length > 0) {
        exercise.sets.forEach((set) => {
          if (weightedSets.includes(exercise.type)) {
            if (set.weight && set.reps) {
              if (set.weight * set.reps > bestSet.calculated) {
                bestSet.output = `${set.weight} x ${set.reps}`;
                bestSet.calculated = set.weight * set.reps;
              }
            }
          }
        });
      }
      if (bestSet.output === "") bestSet.output = "No Sets";

      bestSetList.push(`${exercise.exerciseName}: ${bestSet.output}`);
    });
  }

  return bestSetList;
}
